import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { firebaseConfigured, getFirebase } from '@/firebase';
import { MOCK_THREAD_MESSAGES } from '@/mocks/threadMessages';
import type { ThreadMessage, ThreadSender } from '@/lib/types';

function toMillis(ts: unknown): number {
  if (!ts) return Date.now();
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'object' && ts && 'toMillis' in ts) {
    const m = (ts as { toMillis: unknown }).toMillis;
    if (typeof m === 'function') return (m as () => number)();
  }
  return Date.now();
}

function coerceSender(v: unknown): ThreadSender {
  const s = typeof v === 'string' ? v : 'System';
  switch (s) {
    case 'Discovery':
    case 'Oracle':
    case 'Logos':
    case 'Genesis':
    case 'Orchestrator':
    case 'Founder':
    case 'System':
      return s;
    // Map factory-level role strings onto sender labels.
    case 'user':
      return 'Founder';
    case 'system':
      return 'System';
    case 'agent':
      return 'Discovery';
    default:
      return 'System';
  }
}

export interface ThreadMessagesState {
  messages: ThreadMessage[];
  isMock: boolean;
  loading: boolean;
  error: string | null;
}

// Read-only subscription to factory_runs/{runId}/messages ordered by
// createdAt asc. Falls back to mock transcripts when firebase is
// unconfigured, when a mock runId is passed, or when the subscription
// errors (permission denied / offline / etc).
export function useThreadMessages(runId: string | null): ThreadMessagesState {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) {
      setMessages([]);
      setIsMock(true);
      setLoading(false);
      return;
    }
    // Mock runIds are prefixed with `run_mock_` — short-circuit to mock data
    // so the shell feels alive without hitting Firestore.
    if (runId.startsWith('run_mock_')) {
      setMessages(MOCK_THREAD_MESSAGES[runId] ?? []);
      setIsMock(true);
      setLoading(false);
      return;
    }
    if (!firebaseConfigured) {
      setMessages([]);
      setIsMock(true);
      setLoading(false);
      return;
    }
    const fb = getFirebase();
    if (!fb) return;
    setLoading(true);
    const q = query(
      collection(fb.db, 'factory_runs', runId, 'messages'),
      orderBy('createdAt', 'asc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setMessages(
          snap.docs.map((d): ThreadMessage => {
            const v = d.data() as Record<string, unknown>;
            return {
              id: d.id,
              runId,
              sender: coerceSender(v.sender ?? v.agentName ?? v.role),
              text: (v.text as string) ?? '',
              createdAtMs: toMillis(v.createdAt ?? v.created_at),
              isStageTransition:
                (v.isStageTransition as boolean | undefined) ??
                (v.is_stage_transition as boolean | undefined) ??
                false,
            };
          }),
        );
        setIsMock(false);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setMessages([]);
        setIsMock(true);
        setLoading(false);
        setError(err.message);
      },
    );
    return () => unsub();
  }, [runId]);

  return { messages, isMock, loading, error };
}
